import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from '@vortx/shared-ui';
import { useRetroContextSafe } from "@vortx/shared-ui";
import { useResponsive } from '../../hooks/useResponsive';
import { vortxColors } from '@vortx/shared-ui';
import LightNavigation from './LightNavigation';
import LightActivityFeed from './LightActivityFeed';
import ResponsiveDrawer from '../ResponsiveDrawer';

const LightLayout = () => {
    const location = useLocation();
    const { userProfile } = useAuth();
    const {
        currentProject,
        scenarioTab,
        setScenarioTab,
        phdSection,
        setPhdSection,
        evScenarioTab,
        setEvScenarioTab,
    } = useRetroContextSafe();

    const isDemo = userProfile?.is_demo;
    const isAdmin = !isDemo && (userProfile?.is_super_admin || userProfile?.is_company_admin);
    const isSuperAdmin = userProfile?.is_super_admin;

    const { isLargeDesktop } = useResponsive();

    // Drawer state for narrow screens
    const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);
    const [rightDrawerOpen, setRightDrawerOpen] = useState(false);

    // Close drawers on route change
    useEffect(() => {
        setLeftDrawerOpen(false);
        setRightDrawerOpen(false);
    }, [location.pathname]);

    // Close drawers when resizing to desktop
    useEffect(() => {
        if (isLargeDesktop) {
            setLeftDrawerOpen(false);
            setRightDrawerOpen(false);
        }
    }, [isLargeDesktop]);

    // Only one drawer open at a time
    const handleLeftToggle = () => {
        setRightDrawerOpen(false);
        setLeftDrawerOpen(prev => !prev);
    };

    const handleRightToggle = () => {
        setLeftDrawerOpen(false);
        setRightDrawerOpen(prev => !prev);
    };

    // Height calculation: viewport - header (64px)
    const panelHeight = 'calc(100vh - 64px)';

    return (
        <Box sx={{
            height: panelHeight,
            overflow: 'hidden',
            bgcolor: vortxColors.white,
        }}>
            {/* Main Layout - 3 columns on desktop, 1 on mobile */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: isLargeDesktop
                    ? '240px 1fr 280px'
                    : '1fr',
                gap: 0,
                height: '100%',
            }}>
                {/* Left Panel - Navigation */}
                <ResponsiveDrawer
                    anchor="left"
                    open={leftDrawerOpen}
                    onClose={() => setLeftDrawerOpen(false)}
                    onToggle={handleLeftToggle}
                    desktopSx={{
                        display: { xs: 'none', lg: 'flex' },
                        flexDirection: 'column',
                        borderRight: '1px solid rgba(0, 0, 0, 0.08)',
                        padding: 2,
                        background: 'rgba(0, 0, 0, 0.02)',
                        height: '100%',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(0, 0, 0, 0.1)',
                            borderRadius: '3px',
                            '&:hover': { background: 'rgba(0, 0, 0, 0.2)' },
                        },
                    }}
                >
                    <LightNavigation
                        scenarioTab={scenarioTab}
                        onScenarioTabChange={setScenarioTab}
                        phdSection={phdSection}
                        onPhdSectionChange={setPhdSection}
                        evScenarioTab={evScenarioTab}
                        onEvScenarioTabChange={setEvScenarioTab}
                        isAdmin={isAdmin}
                        isSuperAdmin={isSuperAdmin}
                    />
                </ResponsiveDrawer>

                {/* Center - Main Content (scrollable) */}
                <Box sx={{
                    padding: { xs: 2, md: 3 },
                    height: '100%',
                    overflowY: 'auto',
                    background: vortxColors.white,
                    // Custom scrollbar styling
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(0, 0, 0, 0.1)',
                        borderRadius: '4px',
                        '&:hover': {
                            background: 'rgba(0, 0, 0, 0.2)',
                        },
                    },
                }}>
                    <Outlet />
                </Box>

                {/* Right Panel - Activity Feed */}
                <ResponsiveDrawer
                    anchor="right"
                    open={rightDrawerOpen}
                    onClose={() => setRightDrawerOpen(false)}
                    onToggle={handleRightToggle}
                    desktopSx={{
                        display: { xs: 'none', lg: 'flex' },
                        flexDirection: 'column',
                        borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
                        padding: 2,
                        background: 'rgba(0, 0, 0, 0.02)',
                        height: '100%',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(0, 0, 0, 0.1)',
                            borderRadius: '3px',
                            '&:hover': { background: 'rgba(0, 0, 0, 0.2)' },
                        },
                    }}
                >
                    <LightActivityFeed currentProject={currentProject} />
                </ResponsiveDrawer>
            </Box>
        </Box>
    );
};

export default LightLayout;
